import {
  HttpClient,
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { UnauthenticatedHttpClient } from '@app/auth/services/unauthenticated-http-client.service';
import { AppInjectorService } from '@app/core/services/app-injector.service';
import { Observable } from 'rxjs';

export const MockHelpers = {
  assertUserName<T, R>(fn: (request: HttpRequest<T>, username: string) => R) {
    return function (request: HttpRequest<T>, username?: string) {
      if (username == null) {
        throw { resultCode: 'could_not_extract_username' };
      }

      return fn(request, username);
    };
  },

  enterprisePathRegex(path: string): RegExp {
    return new RegExp(`(?:/enterprise|/.*?enterprise/[a-zA-Z0-9.-]+?)${path}$`);
  },

  getUsername(request: HttpRequest<any>): string | undefined {
    if (request.url.includes('/me')) {
      return AppInjectorService.injector.get(AuthenticationService).authUser.username;
    } else {
      const usernameRegex = /\/.*?enterprise.*?\/users\/(.*?@.*?)\//;
      const result = usernameRegex.exec(request.url);

      if (result != null) {
        return result[1];
      } else {
        return undefined;
      }
    }
  },

  isMockedRequest: new HttpContextToken(() => false),

  /**
   * Helper function to quickly create mocks that save their state to local storage.
   * @param key Local storage key
   * @param defaultValue Default value if the key does not exist in local storage
   */
  locallyStoredValue<T>(key: string, defaultValue?: T): LocallyStoredValue<T> {
    const storedValue: LocallyStoredValue<T> = {
      get() {
        return parseJson(localStorage.getItem(key)) ?? defaultValue;
      },

      update(value) {
        localStorage.setItem(key, JSON.stringify(value));

        return storedValue.get();
      },
    };

    return storedValue;
  },

  sendRequest(request: HttpRequest<any>, httpClient?: HttpClient): Observable<HttpResponse<any>> {
    httpClient ??= AppInjectorService.injector.get(HttpClient);
    const clone = request.clone();
    clone.context.set(MockHelpers.isMockedRequest, true);
    clone.clone();

    return httpClient.request(request.method, request.url, {
      body: request.body,
      context: new HttpContext().set(MockHelpers.isMockedRequest, true),
      headers: request.headers,
      reportProgress: request.reportProgress,
      withCredentials: request.withCredentials,
      observe: 'response',
    });
  },

  sendUnauthenticatedRequest(request: HttpRequest<any>): Observable<HttpResponse<any>> {
    return MockHelpers.sendRequest(request, AppInjectorService.injector.get(UnauthenticatedHttpClient));
  },

  throwBadRequest(error: C5RepositoryError) {
    throw new HttpErrorResponse({
      error,
      status: 400,
      statusText: 'Bad Request',
    });
  },

  throwNotFound(error: C5RepositoryError) {
    throw new HttpErrorResponse({
      error,
      status: 404,
      statusText: 'Not Found',
    });
  },

  throwInternalServerError(error: C5RepositoryError) {
    throw new HttpErrorResponse({
      error,
      status: 500,
      statusText: 'Internal Server Error',
    });
  },

  userPathRegex(path: string): RegExp {
    return new RegExp(`(?:/me|/.*?enterprise/.*?/users/.*?@.*?)${path}$`);
  },
};

function parseJson<T>(s: string): T | null {
  let parsed: T | null = null;

  try {
    parsed = JSON.parse(s);
  } catch {}

  return parsed;
}

export interface LocallyStoredValue<T> {
  get(): T | null;
  update(value: T | null): T | null;
}

interface C5RepositoryError {
  exception?: string;
  hint?: string;
  message?: string;
  resultCode?: string;
  violatingFields?: Record<string, string>[];
}
